/**
 * mediaBlobCache — M2.2.3 · Cache blob URL per media WhatsApp.
 *
 * Motivazione: evitare fetch duplicati dello stesso media durante:
 *   - re-mount (route switch e ritorno)
 *   - passaggio image-bubble → lightbox
 *   - polling che rimescola il set dei messaggi
 *
 * Approccio: Map globale (module-level) `messageId → { promise, url }`.
 * Nessun revoke prematuro; le URL sono valide per la lifecycle della SPA.
 * Rispetta il principio: NON persistere URL Meta (le blob URL sono locali
 * al browser e non contengono l'URL Meta upstream).
 *
 * Non implementa storage permanente (non è service worker cache).
 */
import { api } from "@/lib/api";

const cache = new Map();   // messageId → { promise, url }

/**
 * Ritorna una Promise che risolve nell'oggetto {url, mimeType}.
 * Fetch shared per messaggio: chiamate concorrenti wait sulla stessa promise.
 */
export function getMediaBlobUrl(messageId) {
  if (!messageId) return Promise.reject(new Error("missing messageId"));
  const cached = cache.get(messageId);
  if (cached) return cached.promise;

  const promise = (async () => {
    const res = await api.get(
      `/staff/messaging/whatsapp/messages/${messageId}/media`,
      { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const mimeType = res.data.type || res.headers["content-type"] || null;
    cache.set(messageId, { promise, url, mimeType });
    return { url, mimeType };
  })();

  // Salva subito la promise per dedup dei concorrenti PRIMA della risoluzione.
  cache.set(messageId, { promise, url: null, mimeType: null });
  // Se la promise fallisce, evict per permettere retry
  promise.catch(() => cache.delete(messageId));
  return promise;
}

/** Test/debug helper — evict manuale (usato principalmente nei test). */
export function _evictMediaBlob(messageId) {
  const c = cache.get(messageId);
  if (c && c.url) {
    try { URL.revokeObjectURL(c.url); } catch { /* silent */ }
  }
  cache.delete(messageId);
}

export function _cacheSize() { return cache.size; }
